import { Link } from "react-router-dom";
import { ChevronRight, Check, Layers } from "lucide-react";
import { ICON_COMPONENTS } from "@/lib/iconChoices";
import RichText from "@/components/RichText";

// Repli tant que l'API n'a pas répondu (blocks === undefined) — voir
// ServicesSection.jsx pour la même logique.
const FALLBACK_BLOCKS = [
  { id: "fallback-1", props: { icon: "ShieldCheck", title: "Un premier atout", desc: "Décrivez ici votre premier argument." } },
  { id: "fallback-2", props: { icon: "Zap", title: "Un second atout", desc: "Décrivez ici votre second argument." } },
  { id: "fallback-3", props: { icon: "Package", title: "Un troisième atout", desc: "Décrivez ici votre troisième argument." } },
];

const DEFAULTS = { eyebrow: "", heading: "", subheading: "", variant: "light", anchor_id: "" };

export default function FeatureGridSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  const cards = (blocks === undefined ? FALLBACK_BLOCKS : blocks).filter((b) => b.enabled !== false);
  const dark = t.variant === "dark";
  if (!cards.length && !t.heading) return null;
  return (
    <section id={t.anchor_id || undefined} className={`scroll-mt-28 ${dark ? "bg-ink" : ""}`}>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-28">
        {(t.eyebrow || t.heading || t.subheading) && (
          <div className="text-center max-w-2xl mx-auto">
            {t.eyebrow && (
              <RichText
                as="span"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold tracking-wide uppercase mb-4 ${
                  dark ? "border-white/15 bg-white/5 text-gold" : "border-stone bg-sand text-gold"
                }`}
                html={t.eyebrow}
              />
            )}
            {t.heading && (
              <RichText
                as="h2"
                className={`font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`}
                html={t.heading}
              />
            )}
            {t.subheading && (
              <RichText as="p" preserveParagraphs className={`text-[16px] mt-4 ${dark ? "text-white/60" : "text-graphite"}`} html={t.subheading} />
            )}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {cards.map((block, i) => {
            const Icon = ICON_COMPONENTS[block.props?.icon] || Layers;
            const checklist = (block.props?.checklist || "").split("\n").map((s) => s.trim()).filter(Boolean);
            const ctaLabel = block.props?.cta_label;
            const ctaUrl = block.props?.cta_url || "#";
            return (
              <div
                key={block.id}
                data-block-key={block.id}
                className={`rounded-2xl border p-6 transition-all animate-fade-up ${
                  dark ? "border-white/10 bg-white/[0.04]" : "border-stone bg-white hover:shadow-lg hover:-translate-y-0.5"
                }`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center mb-5">
                  <Icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                </div>
                {block.props?.title && (
                  <RichText as="h3" className={`font-display font-semibold text-[17px] ${dark ? "text-white" : "text-ink"}`} html={block.props.title} />
                )}
                {block.props?.desc && <p className={`text-[14px] mt-2 leading-relaxed ${dark ? "text-white/60" : "text-graphite"}`}>{block.props.desc}</p>}
                {checklist.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {checklist.map((item, k) => (
                      <li key={k} className={`flex items-start gap-2 text-[13.5px] ${dark ? "text-white/70" : "text-graphite"}`}>
                        <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={2} />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {ctaLabel && (
                  ctaUrl.startsWith("/") ? (
                    <Link to={ctaUrl} className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-gold hover:opacity-80 transition-opacity mt-5">
                      {ctaLabel} <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  ) : (
                    <a href={ctaUrl} className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-gold hover:opacity-80 transition-opacity mt-5">
                      {ctaLabel} <ChevronRight className="h-3.5 w-3.5" />
                    </a>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
